import { Trans, useLingui } from "@lingui/react/macro";
import styles from "./Coworkers.module.scss";
import { Heading } from "@medoma/components/Heading/Heading";
import { Loading } from "@medoma/components/Loading/Loading";
import ErrorMessage from "@medoma/components/ErrorMessage/ErrorMessage";
import { useShiftsWithContents } from "@/api/Shifts";
import { startOfToday } from "date-fns";
import { deducedError } from "@/Utils/ErrorUtils";
import * as Sentry from "@sentry/react";
import { ShiftCard } from "./ShiftCard";
import NoResults from "@/components/NoResults/NoResults";
import { useQuery } from "@tanstack/react-query";
import { sitesQueryOptions } from "@/api/Patients";
import { ControlledSelect } from "@medoma/components/Select/Select";
import { useAuth } from "react-oidc-context";
import { useLocalStorage } from "@medoma/shared-employee/useLocalStorage";
import { useSearchParams } from "react-router";
import { SearchPanel } from "./SearchPanel";
import { filterByNameTerms } from "@medoma/components/SearchField/searchUtils";
import { employeeName } from "@medoma/models/shifts";

const ALL_SITES = "";
export const Coworkers = () => {
  const { t } = useLingui();
  const { user } = useAuth();
  if (!user) {
    throw new Error("No user found");
  }
  const userId = user.profile.sub;
  const today = startOfToday();

  const {
    data: sites,
    isPending: isSitesPending,
    isError: isSitesError,
    error: sitesError,
  } = useQuery(sitesQueryOptions);

  const [selectedSite, setSelectedSite] = useLocalStorage({
    key: "coworkers.selectedSite",
    userId,
    initialValue: ALL_SITES,
  });

  const [searchParams] = useSearchParams();
  const searchQuery = searchParams.get("filter") ?? "";

  const {
    data: shiftsData,
    isPending,
    isError,
    error,
  } = useShiftsWithContents(today);

  if (isPending || isSitesPending) {
    return <Loading message={t`Laddar medarbetare`} />;
  }

  if (isSitesError) {
    Sentry.captureException(sitesError);
    return (
      <ErrorMessage
        message={`${t`Kunde inte hämta enheter.`} ${deducedError(sitesError)}`}
      />
    );
  }

  if (isError) {
    Sentry.captureException(error);
    return (
      <ErrorMessage
        message={`${t`Kunde inte hämta medarbetare.`} ${deducedError(error)}`}
      />
    );
  }

  // Shifts without an employee are not relevant here, even if they have items
  const shiftsWithEmployee = shiftsData.filter(
    ({ shift: { employee } }) => employee !== null && employee !== undefined,
  );

  const siteFilteredShifts = selectedSite
    ? shiftsWithEmployee.filter(
        ({ shift: { site } }) => site.id === selectedSite,
      )
    : shiftsWithEmployee;

  const shifts = searchQuery
    ? siteFilteredShifts.filter(({ shift: { employee } }) =>
        employee
          ? filterByNameTerms(
              employeeName(employee, { employeeNameFormat: "full" }),
              searchQuery,
            )
          : false,
      )
    : siteFilteredShifts;

  const selectedSiteDisplayName = sites?.find(
    (s) => s.id === selectedSite,
  )?.displayName;
  const selectedSiteLabel = selectedSiteDisplayName
    ? t`${selectedSiteDisplayName}-enheten`
    : t`vald enhet`;

  return (
    <div className={styles.container}>
      <Heading level="h1">
        <Trans>Medarbetare</Trans>
      </Heading>
      {sites && sites.length > 1 ? (
        <ControlledSelect
          label={t`Enhet`}
          hideLabel
          value={selectedSite}
          onChange={setSelectedSite}
        >
          <option value={ALL_SITES}>{t`Alla enheter`}</option>
          {sites.map((site) => (
            <option key={site.id} value={site.id}>
              {site.displayName}
            </option>
          ))}
        </ControlledSelect>
      ) : null}
      <SearchPanel />
      {shifts.length === 0 ? (
        <NoResults
          message={
            selectedSite
              ? t`Inga medarbetare på ${selectedSiteLabel} idag`
              : t`Inga medarbetare idag`
          }
        />
      ) : (
        <ul className={styles.shiftsList}>
          {shifts.map((shiftWithContents) => (
            <li key={shiftWithContents.shift.id}>
              <ShiftCard shiftWithContents={shiftWithContents} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
